import React from 'react'
import { Button, message, Space } from 'antd'
import useStore from "../../../stores/useStore"
import { DownloadOutlined } from "@ant-design/icons"
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import { renderToCanvas, generateCurrentBackgroundPostcardsCanvas, canvasToDataURL } from '../../../utils/canvasRenderer'

const ExportControl = () => {
    const {
        clearSelection,
        textBlocks,
        csvData,
        backgrounds,
        currentBackgroundId,
        csvFileNameColumn,
        rotation,
        flipX,
        flipY
    } = useStore()

    const getCurrentBackground = () => {
        return backgrounds.find(bg => bg.id === currentBackgroundId)
    }

    const exportSingleImage = async (format = 'png') => {
        const background = getCurrentBackground()
        if (!background) {
            message.warning('Нет фона для экспорта')
            return
        }

        clearSelection()

        try {
            const canvas = document.createElement('canvas')
            canvas.style.display = 'none'

            await renderToCanvas(canvas, background, textBlocks, rotation, flipX, flipY)

            const dataUrl = canvasToDataURL(canvas, format, format === 'jpeg' ? 0.95 : 1.0)

            const link = document.createElement('a')
            link.download = `postcard.${format}`
            link.href = dataUrl
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            message.success(`Открытка успешно экспортирована в ${format.toUpperCase()}`)
        } catch (err) {
            console.error(`Ошибка при экспорте в ${format.toUpperCase()}:`, err)
            message.error(`Ошибка при экспорте в ${format.toUpperCase()}`)
        }
    }

    const exportAllToZip = async () => {
        if (!csvData || csvData.length === 0) {
            message.warning('Нет данных CSV для экспорта')
            return
        }

        const currentBackground = getCurrentBackground()
        if (!currentBackground) {
            message.warning('Выберите фон для экспорта')
            return
        }

        clearSelection()

        try {
            const zip = new JSZip()
            const allPostcards = await generateCurrentBackgroundPostcardsCanvas(
                currentBackground,
                textBlocks,
                csvData,
                csvFileNameColumn
            )

            if (allPostcards.length === 0) {
                message.warning('Нет данных для экспорта')
                return
            }

            let processed = 0

            for (const postcard of allPostcards) {
                const canvas = document.createElement('canvas')
                canvas.style.display = 'none'

                await renderToCanvas(
                    canvas,
                    postcard.background,
                    postcard.textBlocks,
                    rotation,
                    flipX,
                    flipY
                )

                const dataUrl = canvasToDataURL(canvas, 'png', 1.0)

                const base64Data = dataUrl.split(',')[1]
                zip.file(postcard.fileName, base64Data, { base64: true })

                processed++

                if (processed % 10 === 0) {
                    message.info(`Экспортировано ${processed} из ${allPostcards.length} открыток...`)
                }
            }

            const content = await zip.generateAsync({ type: 'blob' })

            const zipName = csvFileNameColumn
                ? `postcards_${currentBackground.name || 'current_background'}_by_${csvFileNameColumn}.zip`
                : `postcards_${currentBackground.name || 'current_background'}_${new Date().toISOString().split('T')[0]}.zip`

            saveAs(content, zipName)

            message.success(`Успешно экспортировано ${allPostcards.length} открыток в архив "${zipName}"`)

        } catch (err) {
            console.error('Ошибка при экспорте:', err)
            message.error('Ошибка при экспорте')
        }
    }

    const currentBackground = getCurrentBackground()
    const totalPostcards = currentBackground ? csvData.length : 0

    return (
        <Space direction="vertical" style={{ width: '100%' }} className="ExportControl-space">
            <h4>Одиночный экспорт</h4>
            <Button
                type="primary"
                onClick={() => exportSingleImage('png')}
                block
                disabled={!currentBackgroundId}
            >
                <DownloadOutlined /> Экспорт в PNG
            </Button>
            <Button
                type="primary"
                onClick={() => exportSingleImage('jpeg')}
                block
                disabled={!currentBackgroundId}
            >
                <DownloadOutlined /> Экспорт в JPEG
            </Button>

            <Space direction="vertical" style={{ width: '100%' }} className="multiple-export">
                <h4>Пакетный экспорт из CSV (PNG)</h4>
                <Button
                    type="primary"
                    onClick={exportAllToZip}
                    block
                    disabled={!csvData?.length || !currentBackground}
                >
                    <DownloadOutlined /> Экспорт всех открыток (ZIP)
                </Button>
                <div style={{ textAlign: 'center', color: '#888' }}>
                    <div>Найдено {csvData.length} записей в CSV</div>
                    <div>Текущий фон: <strong>{currentBackground ? currentBackground.name : 'не выбран'}</strong></div>
                    <div>Будет создано: <strong>{totalPostcards}</strong> открыток</div>
                    {csvFileNameColumn && (
                        <div style={{ color: '#1890ff', marginTop: 4 }}>
                            Файлы будут названы по колонке: <strong>{csvFileNameColumn}</strong>
                        </div>
                    )}
                </div>
            </Space>
        </Space>
    )
}

export default ExportControl